import { MoreHorizontal, Plus, Search } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  deleteTransactionCode,
  listTransactionCodeGroups,
  listTransactionCodes,
  type TransactionCode,
  type TransactionCodeGroup,
  TRANSACTION_CATEGORY_OPTIONS,
  updateGroupCodes,
} from "@/features/reference/api";
import { ApiError } from "@/lib/api";
import { ConfirmDialog } from "./confirm-dialog";
import { ReferencePanel } from "./reference-panel";
import { TransactionCodeDialog } from "./transaction-code-dialog";

const CATEGORY_LABELS = Object.fromEntries(
  TRANSACTION_CATEGORY_OPTIONS.map((o) => [o.value, o.label]),
);

export function TransactionCodesPage() {
  return (
    <ReferencePanel
      title="Transaction Codes"
      description="Account-transcript code vocabulary and the analysis buckets that group them."
    >
      <Tabs defaultValue="codes">
        <TabsList className="mb-4">
          <TabsTrigger value="codes">Codes</TabsTrigger>
          <TabsTrigger value="groups">Category groupings</TabsTrigger>
        </TabsList>
        <TabsContent value="codes">
          <CodesTab />
        </TabsContent>
        <TabsContent value="groups">
          <GroupsTab />
        </TabsContent>
      </Tabs>
    </ReferencePanel>
  );
}

function CodesTab() {
  const [items, setItems] = useState<TransactionCode[]>([]);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<TransactionCode | undefined>();
  const [removing, setRemoving] = useState<TransactionCode | undefined>();

  const refetch = useCallback(() => {
    listTransactionCodes({
      search: search || undefined,
      category: category === "all" ? undefined : category,
      pageSize: 300,
    })
      .then((r) => setItems(r.items))
      .catch(() => toast.error("Couldn't load transaction codes."));
  }, [search, category]);

  useEffect(() => {
    const id = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(id);
  }, [searchInput]);
  useEffect(() => {
    refetch();
  }, [refetch]);

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative w-full sm:w-64">
          <Search className="text-muted-foreground absolute top-1/2 left-3 size-4 -translate-y-1/2" />
          <Input
            placeholder="Search code or description..."
            className="pl-9"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger size="sm" className="w-[200px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {TRANSACTION_CATEGORY_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          size="sm"
          className="ml-auto gap-1.5"
          onClick={() => {
            setEditing(undefined);
            setDialogOpen(true);
          }}
        >
          <Plus className="size-4" /> Add code
        </Button>
      </div>

      {items.length === 0 ? (
        <p className="text-muted-foreground text-sm">No codes match these filters.</p>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Category</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((c) => (
                <TableRow key={c.id}>
                  <TableCell className="font-mono text-xs font-medium">{c.code}</TableCell>
                  <TableCell>{c.description}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {CATEGORY_LABELS[c.category]}
                  </TableCell>
                  <TableCell>
                    <div className="flex justify-end">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size="icon" variant="ghost" aria-label="Code actions">
                            <MoreHorizontal className="size-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onSelect={() => {
                              setEditing(c);
                              setDialogOpen(true);
                            }}
                          >
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem variant="destructive" onSelect={() => setRemoving(c)}>
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <TransactionCodeDialog
        key={editing?.id ?? "new"}
        code={editing}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSaved={refetch}
      />
      {removing && (
        <ConfirmDialog
          title={`Delete code ${removing.code}?`}
          description="Removes it from the vocabulary and any groupings that reference it."
          confirmLabel="Delete"
          destructive
          onConfirm={() => deleteTransactionCode(removing.id)}
          onClose={() => setRemoving(undefined)}
          onDone={refetch}
          successMessage="Code deleted"
          fallbackError="Couldn't delete the code."
        />
      )}
    </div>
  );
}

function GroupsTab() {
  const [groups, setGroups] = useState<TransactionCodeGroup[]>([]);
  const [codes, setCodes] = useState<TransactionCode[]>([]);
  const [selected, setSelected] = useState<string | null>(null);

  const refetch = useCallback(() => {
    Promise.all([listTransactionCodeGroups(), listTransactionCodes({ pageSize: 300 })])
      .then(([g, c]) => {
        setGroups(g);
        setCodes(c.items);
        setSelected((prev) => prev ?? g[0]?.id ?? null);
      })
      .catch(() => toast.error("Couldn't load groupings."));
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const group = groups.find((g) => g.id === selected);

  async function toggleCode(codeId: string, checked: boolean) {
    if (!group) return;
    const next = checked
      ? [...group.codes, codeId]
      : group.codes.filter((id) => id !== codeId);
    // Optimistic update so the checkbox responds immediately.
    setGroups((gs) => gs.map((g) => (g.id === group.id ? { ...g, codes: next } : g)));
    try {
      await updateGroupCodes(group.id, next);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't update the grouping.");
      refetch();
    }
  }

  if (groups.length === 0) {
    return <p className="text-muted-foreground text-sm">No groupings defined.</p>;
  }

  return (
    <div className="grid gap-6 md:grid-cols-[220px_1fr]">
      <div className="flex flex-col gap-1">
        {groups.map((g) => (
          <button
            key={g.id}
            type="button"
            onClick={() => setSelected(g.id)}
            className={`flex items-center justify-between rounded-md px-3 py-2 text-left text-sm ${
              g.id === selected ? "bg-accent text-accent-foreground" : "hover:bg-accent/50"
            }`}
          >
            <span className="font-medium">{g.label}</span>
            <Badge variant="secondary" className="tabular-nums">
              {g.codes.length}
            </Badge>
          </button>
        ))}
      </div>
      {group && (
        <div>
          <p className="text-muted-foreground mb-3 text-sm">
            Codes in the <span className="text-foreground font-medium">{group.label}</span> bucket.
          </p>
          <div className="grid max-h-[420px] grid-cols-1 gap-2 overflow-y-auto sm:grid-cols-2">
            {codes.map((c) => {
              const checked = group.codes.includes(c.id);
              return (
                <label
                  key={c.id}
                  className="hover:bg-accent/40 flex items-start gap-2 rounded-md p-2 text-sm"
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={(v) => toggleCode(c.id, v === true)}
                    className="mt-0.5"
                  />
                  <span>
                    <span className="font-mono text-xs font-medium">{c.code}</span>{" "}
                    <span className="text-muted-foreground">{c.description}</span>
                  </span>
                </label>
              );
            })}
          </div>
          <Label className="sr-only">Grouping membership</Label>
        </div>
      )}
    </div>
  );
}
